/**
 * ORIFOLI専用キャッシュサービス
 * キャラクターページのギャラリー、設定資料、ファンアートのフィードをキャッシュ
 *
 * キャッシュキー形式: orifoli_{feedType}_{characterId}_{userId}
 * - FIGRYNEのキャッシュと被らないようにプレフィックスを使用
 * - ユーザーIDを含めてアカウント別に分離
 */

import { auth } from './firebase';

// キャッシュの有効期限（24時間）
const FEED_CACHE_EXPIRY = 24 * 60 * 60 * 1000;

// ノイズ画像キャッシュの有効期限（30日）
const NOISE_CACHE_EXPIRY = 30 * 24 * 60 * 60 * 1000;

// IndexedDB設定
const DB_NAME = 'orifoliCache';
const DB_VERSION = 1;
const NOISE_STORE_NAME = 'noiseImages';

// データベースインスタンス
let dbInstance: IDBDatabase | null = null;
let isIndexedDBBroken = false;

// フィードタイプ
export type FeedType = 'gallery' | 'reference' | 'fanart';

// キャッシュされたフィードエントリ
export interface CachedFeedEntry {
  modelId: string;
  created: string;
}

// キャッシュされたフィードデータ
interface CachedFeedData {
  entries: CachedFeedEntry[];
  firstTimestamp: string | null;
  lastTimestamp: string | null;
  timestamp: number;
  // ファンアート用の追加メタデータ
  moderationEnabled?: boolean;
}

// キャッシュされたノイズ画像
interface CachedNoiseImage {
  id: string; // modelId_noise1 or modelId_noise2
  modelId: string;
  type: 'noise1' | 'noise2';
  blob: Blob;
  timestamp: number;
}

// 現在のユーザーIDを取得
const getCurrentUserId = (): string => {
  const user = auth.currentUser;
  return user?.uid || 'anonymous';
};

// キャッシュキーを生成
const getCacheKey = (feedType: FeedType, characterId: string): string => {
  const userId = getCurrentUserId();
  return `orifoli_${feedType}_${characterId}_${userId}`;
};

// データベースを開く
const openDatabase = (): Promise<IDBDatabase | null> => {
  return new Promise((resolve) => {
    if (isIndexedDBBroken) {
      resolve(null);
      return;
    }

    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      const timeoutId = setTimeout(() => {
        console.warn('[OrifoliCache] Database open timed out');
        isIndexedDBBroken = true;
        resolve(null);
      }, 5000);

      request.onerror = () => {
        clearTimeout(timeoutId);
        console.warn('[OrifoliCache] Failed to open database:', request.error);
        const errorMsg = request.error?.message?.toLowerCase() || '';
        if (errorMsg.includes('internal error') || errorMsg.includes('backing store') || errorMsg.includes('unknown')) {
          isIndexedDBBroken = true;
        }
        resolve(null);
      };

      request.onsuccess = () => {
        clearTimeout(timeoutId);
        dbInstance = request.result;
        resolve(dbInstance);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(NOISE_STORE_NAME)) {
          const store = db.createObjectStore(NOISE_STORE_NAME, { keyPath: 'id' });
          store.createIndex('modelId', 'modelId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    } catch (error) {
      console.warn('[OrifoliCache] Exception opening database:', error);
      isIndexedDBBroken = true;
      resolve(null);
    }
  });
};

export const orifoliCache = {
  // ========== フィードキャッシュ (localStorage) ==========

  /**
   * フィードエントリをキャッシュに保存
   */
  saveFeedEntries(
    feedType: FeedType,
    characterId: string,
    entries: CachedFeedEntry[],
    firstTimestamp: string | null,
    lastTimestamp: string | null,
    moderationEnabled?: boolean
  ): void {
    try {
      const cacheKey = getCacheKey(feedType, characterId);
      const data: CachedFeedData = {
        entries,
        firstTimestamp,
        lastTimestamp,
        timestamp: Date.now(),
        moderationEnabled,
      };
      localStorage.setItem(cacheKey, JSON.stringify(data));
      console.log(`[OrifoliCache] Feed entries cached: ${feedType}/${characterId} (${entries.length} items)`);
    } catch (error) {
      console.warn('[OrifoliCache] Failed to cache feed entries:', error);
    }
  },

  /**
   * フィードエントリをキャッシュから取得
   */
  getFeedEntries(
    feedType: FeedType,
    characterId: string
  ): CachedFeedData | null {
    try {
      const cacheKey = getCacheKey(feedType, characterId);
      const data = localStorage.getItem(cacheKey);
      if (!data) return null;

      const parsed: CachedFeedData = JSON.parse(data);

      // 有効期限チェック
      if (Date.now() - parsed.timestamp > FEED_CACHE_EXPIRY) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return parsed;
    } catch (error) {
      console.warn('[OrifoliCache] Failed to get feed entries:', error);
      return null;
    }
  },

  /**
   * フィードキャッシュをクリア
   */
  clearFeedCache(feedType: FeedType, characterId: string): void {
    const cacheKey = getCacheKey(feedType, characterId);
    localStorage.removeItem(cacheKey);
    console.log(`[OrifoliCache] Feed cache cleared: ${feedType}/${characterId}`);
  },

  /**
   * フィードから特定のアイテムを削除
   */
  removeFromFeed(feedType: FeedType, characterId: string, modelId: string): boolean {
    try {
      const cacheKey = getCacheKey(feedType, characterId);
      const data = localStorage.getItem(cacheKey);
      if (!data) return false;

      const parsed: CachedFeedData = JSON.parse(data);
      const originalLength = parsed.entries.length;
      parsed.entries = parsed.entries.filter(entry => entry.modelId !== modelId);

      if (parsed.entries.length < originalLength) {
        localStorage.setItem(cacheKey, JSON.stringify(parsed));
        console.log(`[OrifoliCache] Removed from feed: ${feedType}/${characterId}/${modelId}`);
        return true;
      }
      return false;
    } catch (error) {
      console.warn('[OrifoliCache] Failed to remove from feed:', error);
      return false;
    }
  },

  /**
   * フィードにアイテムを追加（先頭に）
   */
  addToFeed(feedType: FeedType, characterId: string, entry: CachedFeedEntry): void {
    try {
      const cacheKey = getCacheKey(feedType, characterId);
      const data = localStorage.getItem(cacheKey);
      if (!data) return;

      const parsed: CachedFeedData = JSON.parse(data);

      // 重複チェック
      if (parsed.entries.some(e => e.modelId === entry.modelId)) {
        return;
      }

      // 先頭に追加
      parsed.entries.unshift(entry);
      parsed.firstTimestamp = entry.created;
      parsed.timestamp = Date.now();

      localStorage.setItem(cacheKey, JSON.stringify(parsed));
      console.log(`[OrifoliCache] Added to feed: ${feedType}/${characterId}/${entry.modelId}`);
    } catch (error) {
      console.warn('[OrifoliCache] Failed to add to feed:', error);
    }
  },

  // ========== ノイズ画像キャッシュ (IndexedDB) ==========

  /**
   * ノイズ画像をキャッシュに保存
   */
  async saveNoiseImages(modelId: string, noise1Url: string, noise2Url: string): Promise<void> {
    try {
      const db = await openDatabase();
      if (!db) return;

      const [noise1Response, noise2Response] = await Promise.all([
        fetch(noise1Url),
        fetch(noise2Url)
      ]);

      if (!noise1Response.ok || !noise2Response.ok) {
        throw new Error('Failed to fetch noise images');
      }

      const [noise1Blob, noise2Blob] = await Promise.all([
        noise1Response.blob(),
        noise2Response.blob()
      ]);

      const timestamp = Date.now();
      const transaction = db.transaction(NOISE_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(NOISE_STORE_NAME);

      const noise1Data: CachedNoiseImage = {
        id: `${modelId}_noise1`,
        modelId,
        type: 'noise1',
        blob: noise1Blob,
        timestamp
      };

      const noise2Data: CachedNoiseImage = {
        id: `${modelId}_noise2`,
        modelId,
        type: 'noise2',
        blob: noise2Blob,
        timestamp
      };

      store.put(noise1Data);
      store.put(noise2Data);

      await new Promise<void>((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });
    } catch (error) {
      console.warn('[OrifoliCache] Failed to cache noise images:', error);
    }
  },

  /**
   * ノイズ画像をキャッシュから取得
   */
  async getNoiseImages(modelId: string): Promise<{ noise1Url: string; noise2Url: string } | null> {
    try {
      const db = await openDatabase();
      if (!db) return null;

      const transaction = db.transaction(NOISE_STORE_NAME, 'readonly');
      const store = transaction.objectStore(NOISE_STORE_NAME);

      const [noise1Data, noise2Data] = await Promise.all([
        new Promise<CachedNoiseImage | undefined>((resolve, reject) => {
          const request = store.get(`${modelId}_noise1`);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        }),
        new Promise<CachedNoiseImage | undefined>((resolve, reject) => {
          const request = store.get(`${modelId}_noise2`);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        })
      ]);

      if (!noise1Data || !noise2Data) {
        return null;
      }

      // 有効期限チェック
      if (Date.now() - noise1Data.timestamp > NOISE_CACHE_EXPIRY) {
        // 期限切れの場合は削除
        this.removeNoiseImages(modelId);
        return null;
      }

      const noise1Url = URL.createObjectURL(noise1Data.blob);
      const noise2Url = URL.createObjectURL(noise2Data.blob);

      return { noise1Url, noise2Url };
    } catch (error) {
      console.warn('[OrifoliCache] Failed to get noise images:', error);
      return null;
    }
  },

  /**
   * ノイズ画像をキャッシュから削除
   */
  async removeNoiseImages(modelId: string): Promise<void> {
    try {
      const db = await openDatabase();
      if (!db) return;

      const transaction = db.transaction(NOISE_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(NOISE_STORE_NAME);

      store.delete(`${modelId}_noise1`);
      store.delete(`${modelId}_noise2`);

      await new Promise<void>((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });
    } catch (error) {
      console.warn('[OrifoliCache] Failed to remove noise images:', error);
    }
  },

  // ========== メタデータキャッシュ (localStorage) ==========

  /**
   * 画像メタデータをキャッシュに保存
   */
  saveImageMetadata(modelId: string, metadata: Record<string, unknown>): void {
    try {
      const key = `orifoli_meta_${modelId}`;
      localStorage.setItem(key, JSON.stringify({
        ...metadata,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('[OrifoliCache] Failed to cache image metadata:', error);
    }
  },

  /**
   * 画像メタデータをキャッシュから取得
   */
  getImageMetadata(modelId: string): Record<string, unknown> | null {
    try {
      const key = `orifoli_meta_${modelId}`;
      const data = localStorage.getItem(key);
      if (!data) return null;

      const parsed = JSON.parse(data);

      // 有効期限チェック（30日）
      if (Date.now() - parsed.timestamp > NOISE_CACHE_EXPIRY) {
        localStorage.removeItem(key);
        return null;
      }

      return parsed;
    } catch (error) {
      console.warn('[OrifoliCache] Failed to get image metadata:', error);
      return null;
    }
  },

  // ========== ユーティリティ ==========

  /**
   * 特定のキャラクターの全キャッシュをクリア
   */
  clearCharacterCache(characterId: string): void {
    const feedTypes: FeedType[] = ['gallery', 'reference', 'fanart'];
    feedTypes.forEach(feedType => {
      this.clearFeedCache(feedType, characterId);
    });
    console.log(`[OrifoliCache] All caches cleared for character: ${characterId}`);
  },

  /**
   * 全キャッシュをクリア
   */
  async clearAll(): Promise<void> {
    try {
      // IndexedDBをクリア
      const db = await openDatabase();
      if (db) {
        const transaction = db.transaction(NOISE_STORE_NAME, 'readwrite');
        const store = transaction.objectStore(NOISE_STORE_NAME);
        store.clear();
        await new Promise<void>((resolve, reject) => {
          transaction.oncomplete = () => resolve();
          transaction.onerror = () => reject(transaction.error);
        });
      }

      // localStorageのORIFOLI関連をクリア
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('orifoli_') || key.startsWith('orifoli_meta_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      console.log('[OrifoliCache] All caches cleared');
    } catch (error) {
      console.warn('[OrifoliCache] Failed to clear all caches:', error);
    }
  },

  /**
   * Blob URLを解放
   */
  revokeNoiseUrls(noise1Url: string, noise2Url: string): void {
    if (noise1Url.startsWith('blob:')) {
      URL.revokeObjectURL(noise1Url);
    }
    if (noise2Url.startsWith('blob:')) {
      URL.revokeObjectURL(noise2Url);
    }
  },

  /**
   * DB接続を閉じる
   */
  closeConnection(): void {
    if (dbInstance) {
      try {
        dbInstance.close();
        console.log('[OrifoliCache] DB connection closed');
      } catch (error) {
        console.error('[OrifoliCache] DB close error:', error);
      }
      dbInstance = null;
    }
    isIndexedDBBroken = false;
  }
};

export default orifoliCache;
